import type { UserPermission } from "@shared/api/authentication/types"
import { PermissionType } from "@shared/api/authentication/types"

/**
 * Checks if user has the required permissions (OR logic - at least one)
 *
 * @param userPermissions - Array of user's permissions
 * @param requiredPermissions - Array of required permissions
 * @returns true if user has at least one required permission
 */
export const hasRequiredPermissions = (
	userPermissions: UserPermission[] | undefined,
	requiredPermissions: PermissionType[],
): boolean => {
	if (!userPermissions || userPermissions.length === 0) {
		return false
	}

	if (requiredPermissions.length === 0) {
		return true
	}

	const userPermissionTypes = new Set(
		userPermissions.map(p => p.permissionType),
	)

	// User must have at least ONE required permission
	return requiredPermissions.some(required =>
		userPermissionTypes.has(required),
	)
}

/**
 * Checks if a permission is expired
 *
 * @param permission - User permission to check
 * @returns true if permission is expired
 */
export const isPermissionExpired = (permission: UserPermission): boolean => {
	if (!permission.expiryDate) {
		return false
	}

	const expiryDate = new Date(permission.expiryDate)
	const now = new Date()

	return expiryDate < now
}

/**
 * Filters out expired permissions
 *
 * @param permissions - Array of user permissions
 * @returns Array of non-expired permissions
 */
export const filterValidPermissions = (
	permissions: UserPermission[] | undefined,
): UserPermission[] | undefined => {
	if (!permissions) {
		return undefined
	}

	return permissions.filter(p => !isPermissionExpired(p))
}
